/**
 * Item Impact Store
 *
 * Manages retrospective business impact events for items.
 * Supabase is the primary data source; this store is a scoped working cache.
 */

import { create } from 'zustand';
import {
  getSafeSupabaseClient,
  generateUUID,
  getCurrentWorkspaceId,
} from '../../lib/supabaseSync';
import type {
  ItemImpactEvent,
  ImpactEventForm,
  ImpactSummary,
  ImpactEventType,
} from '../../types/prioritization/itemImpact';

const TABLE = 'item_impact_events';

/** Stable empty array — prevents new `[]` references when no events are loaded yet */
export const EMPTY_IMPACT_EVENTS: ItemImpactEvent[] = [];

// ── DB → domain mapping ───────────────────────────────────────────────────────

function fromRow(row: Record<string, unknown>): ItemImpactEvent {
  return {
    id: row.id as string,
    workspaceId: row.workspace_id as string,
    itemId: row.item_id as string,
    eventType: row.event_type as ImpactEventType,
    severity: row.severity != null ? Number(row.severity) : undefined,
    impactAmount: row.impact_amount != null ? Number(row.impact_amount) : undefined,
    impactCurrency: (row.impact_currency as string) ?? 'USD',
    description: row.description as string,
    occurredAt: new Date(row.occurred_at as string),
    recordedBy: row.recorded_by as string | undefined,
    createdAt: new Date(row.created_at as string),
    updatedAt: new Date(row.updated_at as string),
  };
}

// ── Store shape ───────────────────────────────────────────────────────────────

interface ItemImpactState {
  /** All fetched events keyed by item id for fast lookup */
  eventsByItem: Record<string, ItemImpactEvent[]>;
  /** Item ids whose events have been fetched at least once */
  fetchedItemIds: Set<string>;

  fetchForItem: (itemId: string) => Promise<void>;
  createEvent: (itemId: string, form: ImpactEventForm, userId?: string) => Promise<ItemImpactEvent | null>;
  deleteEvent: (eventId: string, itemId: string) => Promise<void>;

  /** All events for a given item (empty array if not yet fetched) */
  getEventsForItem: (itemId: string) => ItemImpactEvent[];
  /** Aggregate summary for a given item */
  getSummaryForItem: (itemId: string) => ImpactSummary;
}

// ── Store implementation ──────────────────────────────────────────────────────

export const useItemImpactStore = create<ItemImpactState>((set, get) => ({
  eventsByItem: {},
  fetchedItemIds: new Set(),

  fetchForItem: async (itemId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    const { data, error } = await supabase
      .from(TABLE)
      .select('*')
      .eq('item_id', itemId)
      .order('occurred_at', { ascending: false });

    if (error) {
      console.error('[ItemImpactStore] fetchForItem error:', error);
      return;
    }

    const events = (data ?? []).map((row) => fromRow(row as Record<string, unknown>));

    set((state) => ({
      eventsByItem: { ...state.eventsByItem, [itemId]: events },
      fetchedItemIds: new Set([...state.fetchedItemIds, itemId]),
    }));
  },

  createEvent: async (itemId: string, form: ImpactEventForm, userId?: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return null;

    const workspaceId = getCurrentWorkspaceId();
    if (!workspaceId) {
      console.error('[ItemImpactStore] No workspace id available');
      return null;
    }

    const amountRaw = form.impactAmount ? parseFloat(form.impactAmount) : null;

    const row = {
      id: generateUUID(),
      workspace_id: workspaceId,
      item_id: itemId,
      event_type: form.eventType,
      severity: form.severity ?? null,
      impact_amount: amountRaw != null && !isNaN(amountRaw) ? amountRaw : null,
      impact_currency: form.impactCurrency || 'USD',
      description: form.description.trim(),
      occurred_at: new Date(form.occurredAt).toISOString(),
      recorded_by: userId ?? null,
    };

    // Optimistic insert
    const optimistic = fromRow({ ...row, created_at: new Date().toISOString(), updated_at: new Date().toISOString() });
    set((state) => ({
      eventsByItem: {
        ...state.eventsByItem,
        [itemId]: [optimistic, ...(state.eventsByItem[itemId] ?? [])],
      },
    }));

    const { data, error } = await supabase.from(TABLE).insert(row).select().single();

    if (error) {
      console.error('[ItemImpactStore] createEvent error:', error);
      // Roll back optimistic update
      set((state) => ({
        eventsByItem: {
          ...state.eventsByItem,
          [itemId]: (state.eventsByItem[itemId] ?? []).filter((e) => e.id !== row.id),
        },
      }));
      return null;
    }

    const saved = fromRow(data as Record<string, unknown>);
    set((state) => ({
      eventsByItem: {
        ...state.eventsByItem,
        [itemId]: [saved, ...(state.eventsByItem[itemId] ?? []).filter((e) => e.id !== row.id)],
      },
    }));
    return saved;
  },

  deleteEvent: async (eventId: string, itemId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;

    // Optimistic removal
    set((state) => ({
      eventsByItem: {
        ...state.eventsByItem,
        [itemId]: (state.eventsByItem[itemId] ?? []).filter((e) => e.id !== eventId),
      },
    }));

    const { error } = await supabase.from(TABLE).delete().eq('id', eventId);

    if (error) {
      console.error('[ItemImpactStore] deleteEvent error:', error);
      // Re-fetch to restore state if delete failed
      get().fetchForItem(itemId);
    }
  },

  getEventsForItem: (itemId: string) => {
    return get().eventsByItem[itemId] ?? [];
  },

  getSummaryForItem: (itemId: string): ImpactSummary => {
    const events = get().eventsByItem[itemId] ?? [];
    const count = events.length;

    const withAmounts = events.filter((e) => e.impactAmount != null);
    const totalAmount = withAmounts.length > 0
      ? withAmounts.reduce((sum, e) => sum + (e.impactAmount ?? 0), 0)
      : null;

    const severities = events.map((e) => e.severity).filter((s): s is number => s != null);
    const highestSeverity = severities.length > 0 ? Math.max(...severities) : null;

    const currency = events[0]?.impactCurrency ?? 'USD';

    return { count, totalAmount, currency, highestSeverity };
  },
}));
